import { ModalJustificativaVoto } from 'components/ui/ModalJustificativaVoto';
import ModalWarning from 'components/ui/ModalWarning';
import { VotacaoEtapa1TutorialModal } from 'components/VotacaoEtapa1TutorialModal';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { useVotacaoLock } from 'hooks/useVotacaoLock';
import { HeaderElbb } from 'pages/HomeElbb/components/Header';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaCheck, FaUser } from 'react-icons/fa';
import { IoChevronDown, IoCloudDownloadOutline, IoTimeOutline } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchCategoriasCompletadas,
  submitSelecaoEtapa1,
} from 'services/selecao-etapa1.service';
import { fetchSteps } from 'services/steps.service';
import { getTutorialByTitle } from 'services/tutorial.service';
import {
  buscarValidacaoEtapa1PorPrefixo,
  getIdentifierFromPlayerFields,
  getPrefixoFromPlayerFields,
  hasEtapa1Eligibility,
  IniciativaEtapa1
} from 'services/validacao-etapa1.service';
import { registrarVotar } from 'services/iniciativa-action.service';
import { Tutorial } from 'types/tutorial';
import { StageIds } from 'utils/constants';
import {
  CategoriaVotacao,
  filterIniciativasByCategoria,
  getCategoriasVotacao,
} from 'utils/etapa1-categoria-map';
import { getCategoriaCorHex } from 'utils/iniciativa-categoria';
import {
  exportarIniciativasCsv,
  exportarIniciativasPdf,
  VotacaoEtapa1ExportItem
} from 'utils/votacao-etapa1-export';
import './style.scss';

type TabKey = 'disponiveis' | 'selecionadas';

const TECHNICAL_ERROR_MESSAGE = 'Ocorreu um erro técnico ao carregar as iniciativas. Tente novamente.';

const COMPLIMENT_OPTIONS = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

const JUSTIFICATIVA_STORAGE_KEY = 'votacao_etapa1_justificativas';
const ETAPA_UM_LABEL = 'Etapa 1';

interface RegistroJustificativaVotoEtapa1 {
  etapa: string;
  justificativa: string;
  selectedBy: string;
  selectedById: string;
  selectedAt: string;
  iniciativaIds: string[];
  iniciativaTitulos: string[];
}

interface VotacaoEtapa1EditState {
  editMode?: boolean;
  selecaoId?: string;
  categoria?: string;
  iniciativaIds?: string[];
}

const FALLBACK_START_DATE = new Date('2026-03-23T00:00:00-03:00');
const FALLBACK_END_DATE = new Date('2026-04-04T23:59:59-03:00');

const STAGE_TIMEZONE = 'America/Sao_Paulo';

const isEtapaUmStep = (step?: { _id?: string; title?: string }) => {
  const stepId = String(step?._id || '').toLowerCase();
  const title = String(step?.title || '').toLowerCase();

  return (
    step?._id === StageIds.STAGE_UM_AVALIACAO ||
    stepId === 'step_1' ||
    stepId.includes('step_1') ||
    /etapa\s*1/.test(title) ||
    title.includes('primeira etapa')
  );
};

const formatDateDayMonth = (date: Date) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: STAGE_TIMEZONE
  }).format(date);

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = targetDate.getTime() - Date.now();
    return Math.max(0, diff);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = targetDate.getTime() - Date.now();
      setTimeLeft(Math.max(0, diff));
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  const format = (n: number) => String(n).padStart(2, '0');

  return `${format(days)}:${format(hours)}:${format(minutes)}:${format(seconds)}`;
}

function useVotingPeriod(startDate: Date, endDate: Date) {
  const now = Date.now();
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = now - startDate.getTime();
  const progress =
    totalDuration > 0
      ? Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100)
      : 0;

  return { progress };
}

export function VotacaoEtapa1() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showModal, closeModal } = useModal();
  const [tutorialEtapa1, setTutorialEtapa1] = useState<Tutorial | null>(null);
  const [tabAtiva, setTabAtiva] = useState<TabKey>('disponiveis');
  const [temaSelecionado, setTemaSelecionado] = useState('Todos os temas');
  const [busca, setBusca] = useState('');
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [complimentOpenId, setComplimentOpenId] = useState<string | null>(null);
  const [selectedCompliments, setSelectedCompliments] = useState<Record<string, string>>({});
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [iniciativasNonMad, setIniciativasNonMad] = useState<IniciativaEtapa1[]>([]);
  const [iniciativasMad, setIniciativasMad] = useState<IniciativaEtapa1[]>([]);
  const [iniciativasMadCarregadas, setIniciativasMadCarregadas] = useState(false);
  const [loadingIniciativasApi, setLoadingIniciativasApi] = useState(true);
  const [erroIniciativasApi, setErroIniciativasApi] = useState<string | null>(null);

  const iniciativasApi = useMemo(
    () => [...iniciativasNonMad, ...iniciativasMad],
    [iniciativasNonMad, iniciativasMad]
  );
  const [votingDates, setVotingDates] = useState({
    startDate: FALLBACK_START_DATE,
    endDate: FALLBACK_END_DATE
  });
  const [loadingPeriodoEtapa1, setLoadingPeriodoEtapa1] = useState(true);
  const complimentRef = useRef<HTMLDivElement>(null);
  const editHydratedRef = useRef(false);
  const editLockAttemptedRef = useRef(false);
  const editGuardHandledRef = useRef(false);

  const { progress } = useVotingPeriod(votingDates.startDate, votingDates.endDate);
  const startDateLabel = formatDateDayMonth(votingDates.startDate);
  const endDateLabel = formatDateDayMonth(votingDates.endDate);
  const countdown = useCountdown(votingDates.endDate);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    async function carregarTutorialEtapa1() {
      try {
        const tutorial = await getTutorialByTitle('TUTORIAL_ETAPA1');
        setTutorialEtapa1(tutorial);
      } catch {
        setTutorialEtapa1(null);
      }
    }

    carregarTutorialEtapa1();
  }, []);

  useEffect(() => {
    async function carregarPeriodoEtapaUm() {
      try {
        const steps = await fetchSteps();
        const etapaUm = steps.find((step) => isEtapaUmStep(step));

        if (!etapaUm) {
          return;
        }

        const startDate = new Date(etapaUm.startDate);
        const endDate = new Date(etapaUm.endDate);

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          return;
        }

        setVotingDates({ startDate, endDate });
      } catch {
        setVotingDates({
          startDate: FALLBACK_START_DATE,
          endDate: FALLBACK_END_DATE
        });
      } finally {
        setLoadingPeriodoEtapa1(false);
      }
    }

    carregarPeriodoEtapaUm();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setComplimentOpenId(null);
      }
    };

    if (complimentOpenId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [complimentOpenId]);

  const isElegivelEtapa1 = useMemo(() => hasEtapa1Eligibility(user?.detail), [user]);
  const prefixoUsuario = useMemo(() => getPrefixoFromPlayerFields(user?.detail), [user]);
  const identifier = useMemo(() => getIdentifierFromPlayerFields(user?.detail), [user]);
  const hasIdentifier = Boolean(identifier.comite || identifier.prefixo);
  const escopoLabel = identifier.comite ? 'Comitê' : 'Prefixo';
  const escopoValor = identifier.comite || prefixoUsuario;

  const etapa1Array: string[] = useMemo(
    () => Array.isArray(user?.detail?.etapa1) ? user.detail.etapa1 : [],
    [user]
  );

  const [categoriasCompletadas, setCategoriasCompletadas] = useState<Set<string>>(new Set());
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [categoriaSelecionadaKey, setCategoriaSelecionadaKey] = useState<string>('');

  const editState = (location.state as VotacaoEtapa1EditState | null) || null;
  const isEtapa1Ativa = useMemo(() => {
    const agora = Date.now();
    return agora >= votingDates.startDate.getTime() && agora <= votingDates.endDate.getTime();
  }, [votingDates]);

  const isEdicaoSolicitada = useMemo(() => {
    return Boolean(
      editState?.editMode &&
      editState?.selecaoId &&
      editState?.categoria &&
      Array.isArray(editState?.iniciativaIds)
    );
  }, [editState]);

  const categoriasVotacao = useMemo(
    () => {
      const todas = getCategoriasVotacao(etapa1Array, iniciativasApi);
      return todas.filter(
        (cat) => cat.isMad || filterIniciativasByCategoria(iniciativasApi, cat).length > 0
      );
    },
    [etapa1Array, iniciativasApi]
  );

  const categoriaEdicao = useMemo(() => {
    if (!isEdicaoSolicitada || !editState?.categoria) {
      return null;
    }

    return categoriasVotacao.find((c) => c.etapa1Key === editState.categoria) || null;
  }, [categoriasVotacao, editState?.categoria, isEdicaoSolicitada]);

  const isEditModeAtivo = Boolean(
    isEdicaoSolicitada &&
    !loadingPeriodoEtapa1 &&
    isEtapa1Ativa &&
    categoriaEdicao
  );

  const primeiraCategoriaPendente: CategoriaVotacao | null = useMemo(() => {
    if (isEditModeAtivo && categoriaEdicao) {
      return categoriaEdicao;
    }
    return categoriasVotacao.find((c) => !categoriasCompletadas.has(c.etapa1Key)) || null;
  }, [categoriaEdicao, categoriasVotacao, categoriasCompletadas, isEditModeAtivo]);

  const categoriaAtual: CategoriaVotacao | null = useMemo(() => {
    if (isEditModeAtivo && categoriaEdicao) {
      return categoriaEdicao;
    }

    if (categoriaSelecionadaKey) {
      const escolhida = categoriasVotacao.find((c) => c.etapa1Key === categoriaSelecionadaKey);
      const bloqueada = escolhida
        ? categoriasCompletadas.has(escolhida.etapa1Key)
        : false;

      if (escolhida && !bloqueada) {
        return escolhida;
      }
    }
    return primeiraCategoriaPendente;
  }, [categoriaEdicao, categoriasVotacao, categoriaSelecionadaKey, categoriasCompletadas, isEditModeAtivo, primeiraCategoriaPendente]);

  useEffect(() => {
    if (isEditModeAtivo && categoriaEdicao) {
      setCategoriaSelecionadaKey(categoriaEdicao.etapa1Key);
      return;
    }

    if (!categoriaSelecionadaKey && primeiraCategoriaPendente) {
      setCategoriaSelecionadaKey(primeiraCategoriaPendente.etapa1Key);
    }
  }, [categoriaEdicao, primeiraCategoriaPendente, categoriaSelecionadaKey, isEditModeAtivo]);

  useEffect(() => {
    if (!isEdicaoSolicitada || editGuardHandledRef.current || loadingPeriodoEtapa1 || loadingIniciativasApi) {
      return;
    }

    if (!isEtapa1Ativa) {
      editGuardHandledRef.current = true;
      showModal(
        'votacao-etapa1-edicao-indisponivel',
        <ModalWarning
          title="Edição indisponível"
          description="A edição da seleção só pode ser feita durante o período ativo da Etapa 1."
          btnText="Fechar"
          onClose={() => {
            closeModal();
            navigate('/meus-votos', { replace: true });
          }}
        />,
        'votacao-etapa1-edicao-indisponivel-wrapper',
        false
      );
      return;
    }

    if (!categoriaEdicao) {
      editGuardHandledRef.current = true;
      showModal(
        'votacao-etapa1-edicao-categoria-invalida',
        <ModalWarning
          title="Não foi possível abrir a edição"
          description="Não foi possível abrir a edição para a categoria informada."
          btnText="Fechar"
          onClose={() => {
            closeModal();
            navigate('/meus-votos', { replace: true });
          }}
        />,
        'votacao-etapa1-edicao-categoria-invalida-wrapper',
        false
      );
    }
  }, [categoriaEdicao, closeModal, isEdicaoSolicitada, isEtapa1Ativa, loadingIniciativasApi, loadingPeriodoEtapa1, navigate, showModal]);

  const todasCategoriasCompletas = categoriasVotacao.length > 0 &&
    categoriasVotacao.every((c) => categoriasCompletadas.has(c.etapa1Key));

  const {
    lockState,
    lockHolder,
    acquireLock: acquireVotingLock,
    completeLock: completeVotingLock,
    refreshLockStatus,
  } = useVotacaoLock(identifier, categoriaAtual?.etapa1Key || '', user);

  useEffect(() => {
    async function checkCategorias() {
      if (!hasIdentifier || etapa1Array.length === 0) {
        setLoadingCategorias(false);
        return;
      }
      try {
        const completadas = await fetchCategoriasCompletadas(identifier, etapa1Array);
        setCategoriasCompletadas(new Set(completadas));
      } catch {
        // best-effort
      } finally {
        setLoadingCategorias(false);
      }
    }
    checkCategorias();
  }, [identifier, hasIdentifier, etapa1Array]);

  const carregarIniciativasEtapa1 = useCallback(async () => {
    if (!isElegivelEtapa1 || !hasIdentifier) {
      setIniciativasNonMad([]);
      setErroIniciativasApi(TECHNICAL_ERROR_MESSAGE);
      setLoadingIniciativasApi(false);
      return;
    }

    setLoadingIniciativasApi(true);
    setErroIniciativasApi(null);

    try {
      const lista = await buscarValidacaoEtapa1PorPrefixo(identifier);

      if (!Array.isArray(lista) || lista.length === 0) {
        setIniciativasNonMad([]);
        setErroIniciativasApi(TECHNICAL_ERROR_MESSAGE);
        return;
      }

      const listaSemMad = lista.filter((item) => !item.isMad);
      setIniciativasNonMad(listaSemMad);
      setSelecionadas((anterior) =>
        anterior.filter((id) => listaSemMad.some((item) => item.id === id))
      );
    } catch {
      setIniciativasNonMad([]);
      setErroIniciativasApi(TECHNICAL_ERROR_MESSAGE);
    } finally {
      setLoadingIniciativasApi(false);
    }
  }, [isElegivelEtapa1, hasIdentifier, identifier]);

  const carregarIniciativasMad = useCallback(async () => {
    if (!isElegivelEtapa1 || !hasIdentifier) return;

    setLoadingIniciativasApi(true);
    setErroIniciativasApi(null);

    try {
      const lista = await buscarValidacaoEtapa1PorPrefixo(identifier);
      setIniciativasMad(Array.isArray(lista) ? lista : []);
      setIniciativasMadCarregadas(true);
    } catch {
      setIniciativasMad([]);
      setIniciativasMadCarregadas(true);
      setErroIniciativasApi(TECHNICAL_ERROR_MESSAGE);
    } finally {
      setLoadingIniciativasApi(false);
    }
  }, [isElegivelEtapa1, hasIdentifier, identifier]);

  useEffect(() => {
    carregarIniciativasEtapa1();
  }, [carregarIniciativasEtapa1]);

  useEffect(() => {
    const temMad = etapa1Array.some((key) => String(key).toUpperCase() === 'MAD');
    if (temMad && !iniciativasMadCarregadas) {
      carregarIniciativasMad();
    }
  }, [etapa1Array, iniciativasMadCarregadas, carregarIniciativasMad]);

  const iniciativasCategoria = useMemo(
    () => categoriaAtual ? filterIniciativasByCategoria(iniciativasApi, categoriaAtual) : [],
    [iniciativasApi, categoriaAtual]
  );

  useEffect(() => {
    if (!isEditModeAtivo || editHydratedRef.current) {
      return;
    }

    const idsEdicao = new Set(Array.isArray(editState?.iniciativaIds) ? editState.iniciativaIds : []);
    const idsValidosCategoria = iniciativasCategoria
      .filter((item) => idsEdicao.has(item.id))
      .map((item) => item.id);

    setSelecionadas(idsValidosCategoria);
    setTabAtiva('disponiveis');
    editHydratedRef.current = true;
  }, [editState?.iniciativaIds, iniciativasCategoria, isEditModeAtivo]);

  useEffect(() => {
    async function acquireEditLockIfNeeded() {
      if (!isEditModeAtivo || !editHydratedRef.current || editLockAttemptedRef.current || selecionadas.length === 0) {
        return;
      }

      editLockAttemptedRef.current = true;
      await acquireVotingLock();
    }

    acquireEditLockIfNeeded();
  }, [acquireVotingLock, isEditModeAtivo, selecionadas.length]);

  const temas = useMemo(() => ['Todos os temas', ...Array.from(new Set(iniciativasCategoria.map((item) => item.tema)))], [iniciativasCategoria]);

  const iniciativasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return iniciativasCategoria.filter((item) => {
      const matchTema = temaSelecionado === 'Todos os temas' || item.tema === temaSelecionado;
      const matchBusca =
        termo.length === 0 ||
        item.titulo.toLowerCase().includes(termo) ||
        item.descricao.toLowerCase().includes(termo);

      return matchTema && matchBusca;
    });
  }, [busca, iniciativasCategoria, temaSelecionado]);

  const iniciativasDisponiveis = useMemo(
    () => iniciativasFiltradas,
    [iniciativasFiltradas]
  );

  const iniciativasSelecionadas = useMemo(
    () => iniciativasFiltradas.filter((item) => selecionadas.includes(item.id)),
    [iniciativasFiltradas, selecionadas]
  );

  const iniciativasRenderizadas = tabAtiva === 'disponiveis' ? iniciativasDisponiveis : iniciativasSelecionadas;
  const totalIniciativas = iniciativasCategoria.length;
  const totalSelecionadas = selecionadas.length;

  const SIMPLIFIQUE_TARGET = 30;
  const ELBB_SELECTION_TARGET = 3;
  const isCategoriaSimplifique = (categoriaAtual?.etapa1Key || '').toUpperCase() === 'SIMPLIFIQUE';
  const isCategoriaElbb = (categoriaAtual?.etapa1Key || '').toUpperCase() === 'ELBB';
  const maxSelecionadas = isCategoriaSimplifique
    ? Math.min(SIMPLIFIQUE_TARGET, totalIniciativas)
    : isCategoriaElbb
      ? Math.min(ELBB_SELECTION_TARGET, totalIniciativas)
      : Math.ceil(totalIniciativas / 2);

  const percentualSelecionado = totalIniciativas > 0
    ? Math.round((totalSelecionadas / totalIniciativas) * 100)
    : 0;
  const percentualMinimo = 50;
  const atingiuMinimo = (isCategoriaSimplifique || isCategoriaElbb)
    ? totalSelecionadas >= maxSelecionadas
    : percentualSelecionado >= percentualMinimo;
  const atingiuLimite = maxSelecionadas > 0 && totalSelecionadas >= maxSelecionadas;
  const canSendVotes = totalSelecionadas > 0 && atingiuMinimo;

  const toggleSelecao = useCallback(async (id: string) => {
    const isAdding = !selecionadas.includes(id);
    const shouldAcquireLock =
      lockState !== 'locked_by_self' &&
      (isEditModeAtivo || (isAdding && selecionadas.length === 0));

    if (shouldAcquireLock) {
      const acquired = await acquireVotingLock();
      if (!acquired) return;
    }

    setSelecionadas((anterior) =>
      anterior.includes(id) ? anterior.filter((itemId) => itemId !== id) : [...anterior, id]
    );
  }, [selecionadas, lockState, isEditModeAtivo, acquireVotingLock]);

  const removerSelecao = useCallback(async (id: string) => {
    if (isEditModeAtivo && lockState !== 'locked_by_self') {
      const acquired = await acquireVotingLock();
      if (!acquired) return;
    }

    setSelecionadas((anterior) => anterior.filter((itemId) => itemId !== id));
    setTabAtiva('disponiveis');
  }, [acquireVotingLock, isEditModeAtivo, lockState]);

  const handleComplimentSelect = useCallback((iniciativaId: string, compliment: string) => {
    setSelectedCompliments((prev) => ({ ...prev, [iniciativaId]: compliment }));
    setComplimentOpenId(null);
  }, []);

  const handleToggleFavorite = useCallback((id: string) => {
    setFavoritedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const abrirTutorial = () => {
    if (!tutorialEtapa1?.steps?.length) {
      return;
    }

    showModal(
      'votacao-etapa1-tutorial-modal',
      <VotacaoEtapa1TutorialModal
        steps={tutorialEtapa1.steps}
        onClose={closeModal}
        onStartVoting={closeModal}
      />,
      'votacao-etapa1-tutorial-wrapper',
      false
    );
  };

  const acessarSelecionadas = () => {
    setTabAtiva('selecionadas');
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const selectedById = useMemo(() => {
    return String(user?.detail?.acessor?.vp?.matricula || user?._id || '').trim();
  }, [user]);

  const selectedByName = useMemo(() => {
    const nome = String(user?.name || '').trim();
    if (nome && selectedById) {
      return `${nome} (${selectedById})`;
    }

    if (nome) {
      return nome;
    }

    return selectedById || 'Usuário não identificado';
  }, [selectedById, user]);

  const iniciativasParaExportacao = useMemo<VotacaoEtapa1ExportItem[]>(() => {
    return iniciativasDisponiveis.map((iniciativa) => ({
      titulo: iniciativa.titulo,
      descricao: iniciativa.descricao,
      resultado: iniciativa.resumoTexto,
      videoUrl: iniciativa.videoUrlOriginal || iniciativa.videoUrl,
      tema: iniciativa.tema || '',
      categoryPremio: iniciativa.categoryPremio || '',
    }));
  }, [iniciativasDisponiveis]);

  const registrarJustificativaLocal = useCallback((justificativa: string, selectedAt: Date) => {
    const registro: RegistroJustificativaVotoEtapa1 = {
      etapa: ETAPA_UM_LABEL,
      justificativa,
      selectedBy: selectedByName,
      selectedById,
      selectedAt: selectedAt.toISOString(),
      iniciativaIds: iniciativasSelecionadas.map((item) => item.id),
      iniciativaTitulos: iniciativasSelecionadas.map((item) => item.titulo)
    };

    try {
      const registrosSalvos = localStorage.getItem(JUSTIFICATIVA_STORAGE_KEY);
      const listaAtual: RegistroJustificativaVotoEtapa1[] = registrosSalvos
        ? JSON.parse(registrosSalvos)
        : [];

      listaAtual.push(registro);
      localStorage.setItem(JUSTIFICATIVA_STORAGE_KEY, JSON.stringify(listaAtual));
    } catch {
      localStorage.setItem(JUSTIFICATIVA_STORAGE_KEY, JSON.stringify([registro]));
    }
  }, [iniciativasSelecionadas, selectedById, selectedByName]);

  const handleExportarPdf = useCallback(() => {
    exportarIniciativasPdf(iniciativasParaExportacao, 'iniciativas-selecionadas-etapa1');
  }, [iniciativasParaExportacao]);

  const handleExportarPlanilha = useCallback(() => {
    exportarIniciativasCsv(iniciativasParaExportacao, 'iniciativas-selecionadas-etapa1');
  }, [iniciativasParaExportacao]);

  const abrirModalAviso = useCallback((titulo: string, descricao: string, onConfirm?: () => void) => {
    showModal(
      'votacao-etapa1-aviso',
      <ModalWarning
        title={titulo}
        description={descricao}
        btnText="Fechar"
        onClose={() => {
          closeModal();
          onConfirm?.();
        }}
      />,
      'votacao-etapa1-aviso-wrapper',
      false
    );
  }, [closeModal, showModal]);

  const handleAbrirModalJustificativa = useCallback(() => {
    const selectedAt = new Date();

    showModal(
      'votacao-etapa1-justificativa',
      <ModalJustificativaVoto
        etapaLabel={ETAPA_UM_LABEL}
        selecionadoPor={selectedByName}
        timestamp={selectedAt}
        onCancel={closeModal}
        onConfirm={async (justificativa: string) => {
          if (!categoriaAtual) return;
          try {
            const payloadBase = {
              ...(identifier.comite ? { comite: identifier.comite } : { prefixo: identifier.prefixo || prefixoUsuario }),
              categoria: categoriaAtual.etapa1Key,
              selectedBy: user?._id || '',
              selectedByName,
              selectedByMatricula: selectedById,
              selectedAt: Date.now(),
              justificativa,
              iniciativaIds: iniciativasSelecionadas.map((i) => i.id),
              iniciativaTitulos: iniciativasSelecionadas.map((i) => i.titulo),
              totalIniciativas,
              totalSelecionadas,
            };

            await submitSelecaoEtapa1(payloadBase);

            await completeVotingLock();

            registrarJustificativaLocal(justificativa, selectedAt);

            closeModal();

            const novasCompletadas = new Set(Array.from(categoriasCompletadas).concat(categoriaAtual.etapa1Key));
            setCategoriasCompletadas(novasCompletadas);

            const proximaPendente = categoriasVotacao.find((c) => !novasCompletadas.has(c.etapa1Key));
            if (proximaPendente) {
              setSelecionadas([]);
              setTabAtiva('disponiveis');
              setTemaSelecionado('Todos os temas');
              setBusca('');
              window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            } else {
              if (user?._id) {
                try {
                  await registrarVotar(user._id);
                } catch (actionLogError) {
                  console.warn('Falha ao registrar action_log votar (Etapa 1):', actionLogError);
                }
              }
              navigate('/confirmacao-voto-etapa1');
            }
          } catch {
            abrirModalAviso('Erro ao enviar seleção', 'Tente novamente.');
          }
        }}
      />,
      'votacao-etapa1-justificativa-wrapper',
      false
    );
  }, [abrirModalAviso, categoriaAtual, categoriasCompletadas, categoriasVotacao, closeModal, completeVotingLock, identifier, iniciativasSelecionadas, navigate, prefixoUsuario, registrarJustificativaLocal, selectedById, selectedByName, showModal, totalIniciativas, totalSelecionadas, user]);

  const shouldShowConclusao = todasCategoriasCompletas && !loadingCategorias && !isEditModeAtivo;

  useEffect(() => {
    if (!isCategoriaElbb) return;
    if (loadingIniciativasApi) return;
    if (isEditModeAtivo) return;
    if (totalIniciativas === 0 || totalIniciativas >= ELBB_SELECTION_TARGET) return;
    setSelecionadas(iniciativasCategoria.map((item) => item.id));
  }, [isCategoriaElbb, loadingIniciativasApi, isEditModeAtivo, totalIniciativas, iniciativasCategoria]);

  return (
    <div id="votacao-etapa-1-wrapper">
      <HeaderElbb currentPage="inicio" />

      {lockState === 'locked_by_other' && lockHolder && (
        <div className="max-w-[1200px] mx-auto px-4 mt-6" role="alert" aria-live="assertive">
          <div className="bg-white rounded-lg overflow-hidden border border-gray-bb-5 shadow-sm">
            <div className="p-4 md:p-5 border-b border-gray-bb-5">
              <h2 className="text-blue-bb-1 text-[26px] md:text-[30px] font-title font-bold leading-tight">
                Votação em andamento
              </h2>
              <p className="text-[#2C323F] text-sm md:text-base mt-2">
                Outro gestor do seu {escopoLabel.toLowerCase()} já está realizando a seleção de iniciativas.
              </p>
            </div>

            <div className="px-4 md:px-5 py-4 border-b border-gray-bb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                  <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Seleção sendo realizada por</span>
                  <span className="block text-sm text-[#111214] font-medium mt-1">{lockHolder.name} ({lockHolder.matricula})</span>
                </div>
                <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                  <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">{escopoLabel}</span>
                  <span className="block text-sm text-[#111214] font-medium mt-1">{escopoValor}</span>
                </div>
              </div>

              <p className="text-[#6B7280] text-sm mt-4">
                Aguarde a conclusão ou entre em contato com o gestor responsável.
                Esta página verifica automaticamente a cada 30 segundos.
              </p>
            </div>

            <div className="flex justify-end p-4 md:p-5">
              <button
                type="button"
                className="bg-yellow-bb text-blue-bb-1 rounded-[4px] px-4 py-2 text-sm font-title font-bold uppercase hover:brightness-95 transition-all"
                onClick={refreshLockStatus}
              >
                Verificar agora
              </button>
            </div>
          </div>
        </div>
      )}

      {shouldShowConclusao && (
        <div className="max-w-[1200px] mx-auto px-4 mt-6" role="status">
          <div className="bg-white rounded-lg overflow-hidden border border-gray-bb-5 shadow-sm">
            <div className="p-4 md:p-5 border-b border-gray-bb-5">
              <h2 className="text-blue-bb-1 text-[26px] md:text-[30px] font-title font-bold leading-tight">
                Seleção concluída
              </h2>
              <p className="text-[#2C323F] text-sm md:text-base mt-2">
                A seleção de iniciativas para o seu {escopoLabel.toLowerCase()} já foi realizada
                {categoriasVotacao.length > 1
                  ? ` em todas as ${categoriasVotacao.length} categorias.`
                  : '.'}
              </p>
            </div>

            <div className="px-4 md:px-5 py-4 border-b border-gray-bb-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                  <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">Categorias concluídas</span>
                  <span className="block text-sm text-[#111214] font-medium mt-1">
                    {categoriasVotacao.map((c) => c.label).join(', ')}
                  </span>
                </div>
                <div className="border border-[#E0E7FF] rounded-md p-3 bg-[#F8FAFF]">
                  <span className="block text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">{escopoLabel}</span>
                  <span className="block text-sm text-[#111214] font-medium mt-1">{escopoValor}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-4 md:p-5">
              <button
                type="button"
                className="bg-yellow-bb text-blue-bb-1 rounded-[4px] px-4 py-2 text-sm font-title font-bold uppercase hover:brightness-95 transition-all"
                onClick={() => navigate('/meus-votos')}
              >
                Ver seleção
              </button>
            </div>
          </div>
        </div>
      )}

      {lockState !== 'locked_by_other' && (!todasCategoriasCompletas || isEditModeAtivo) && (<>
      <section className="votacao-etapa-1-hero">
        <div className="votacao-etapa-1-hero__content">
          <h1 className="votacao-etapa-1-hero__title">Etapa 1</h1>
          <p className="votacao-etapa-1-hero__description">
            Ajude a reconhecer o trabalho de quem faz a diferença! Dentre as iniciativas disponíveis,
            {isCategoriaSimplifique
              ? ` selecione ${SIMPLIFIQUE_TARGET} das que considera mais impactantes para o BB.`
              : isCategoriaElbb
                ? ` selecione ${ELBB_SELECTION_TARGET} das que considera mais impactantes para o BB.`
                : ' selecione 50% das que considera mais impactante para o BB.'}
          </p>
          <button className="votacao-etapa-1-hero__link" type="button" onClick={abrirTutorial}>
            Saiba aqui como fazer a seleção!
          </button>

          <div className="votacao-etapa-1-hero__timer">
            <span className="votacao-etapa-1-hero__timer-label">
              O PERÍODO DE SELEÇÃO SERÁ FINALIZADO EM:
            </span>
            <span className="votacao-etapa-1-hero__timer-badge">
              <IoTimeOutline />
              {countdown}
            </span>
          </div>

          <div className="votacao-etapa-1-hero__progress">
            <span className="votacao-etapa-1-hero__progress-date">De {startDateLabel}</span>
            <div className="votacao-etapa-1-hero__progress-bar">
              <div
                className="votacao-etapa-1-hero__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="votacao-etapa-1-hero__progress-date">Até {endDateLabel}</span>
          </div>
        </div>
      </section>

      {categoriasVotacao.length > 1 && categoriaAtual && (
        <div className="max-w-[1200px] mx-auto px-4 mt-4">
          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-4 flex flex-col gap-3 shadow-sm">
            <p className="text-sm text-[#2C323F] font-semibold leading-snug">
              Você faz parte do comitê de seleção de {categoriasVotacao.length} categorias.
              Selecione a categoria e faça a seleção.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[#2C323F] font-bold whitespace-nowrap mr-1">
                Categoria:
              </span>
              {categoriasVotacao.map((cat) => {
                const isCompleted = categoriasCompletadas.has(cat.etapa1Key);
                const isCurrent = cat.etapa1Key === categoriaAtual.etapa1Key;
                return (
                  <button
                    key={cat.etapa1Key}
                    type="button"
                    disabled={isCompleted}
                    onClick={() => {
                      if (isCompleted || isCurrent) return;
                      setCategoriaSelecionadaKey(cat.etapa1Key);
                      setSelecionadas([]);
                      setTabAtiva('disponiveis');
                      setBusca('');
                      setTemaSelecionado('Todos os temas');
                      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                      isCompleted
                        ? 'bg-green-50 border-green-400 text-green-700 cursor-not-allowed'
                        : isCurrent
                          ? 'bg-blue-bb-1 border-blue-bb-1 text-white cursor-default'
                          : 'bg-white border-yellow-500 text-[#2C323F] hover:bg-yellow-50 cursor-pointer'
                    }`}
                  >
                    {isCompleted && <FaCheck size={10} />}
                    {cat.label}
                    {isCompleted ? ' (concluída)' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <main className="votacao-etapa-1-main">
        <section className="votacao-etapa-1-content" aria-live="polite">
          <p className="votacao-etapa-1-content__counter">
            Iniciativas selecionadas {totalSelecionadas}/{totalIniciativas}
          </p>

          <div className="votacao-etapa-1-tabs">
            <button
              className={`votacao-etapa-1-tabs__item ${tabAtiva === 'disponiveis' ? 'is-active' : ''}`}
              onClick={() => setTabAtiva('disponiveis')}
              type="button"
            >
              DISPONÍVEIS ({iniciativasDisponiveis.length})
            </button>
            <button
              className={`votacao-etapa-1-tabs__item ${tabAtiva === 'selecionadas' ? 'is-active' : ''}`}
              onClick={() => setTabAtiva('selecionadas')}
              type="button"
            >
              SELECIONADAS ({iniciativasSelecionadas.length})
            </button>
          </div>

          {tabAtiva === 'selecionadas' && !atingiuMinimo && (
            <div className="votacao-etapa-1-empty-tab">
              <h2 className="votacao-etapa-1-empty-tab__title">
                {(isCategoriaSimplifique || isCategoriaElbb)
                  ? `Para habilitar essa aba é necessário selecionar ${maxSelecionadas} iniciativas.`
                  : 'Para habilitar essa aba é necessário selecionar 50% das iniciativas.'}
              </h2>
            </div>
          )}

          {(tabAtiva === 'disponiveis' || atingiuMinimo) && (
            <>
              <div className="votacao-etapa-1-filters">
                <select
                  value={temaSelecionado}
                  onChange={(event) => setTemaSelecionado(event.target.value)}
                  className="votacao-etapa-1-filters__select"
                >
                  {temas.map((tema) => (
                    <option key={tema} value={tema}>{tema}</option>
                  ))}
                </select>
                <input
                  type="search"
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                  placeholder="O que você procura?"
                  className="votacao-etapa-1-filters__search"
                />
              </div>

              {tabAtiva === 'disponiveis' && (
                <div className="votacao-etapa-1-footer-actions votacao-etapa-1-footer-actions--top">
                  <div className="votacao-etapa-1-footer-actions__right-group">
                    <button
                      type="button"
                      className="votacao-etapa-1-footer-actions__export"
                      onClick={handleExportarPdf}
                    >
                      EXPORTAR PDF
                    </button>
                    <button
                      type="button"
                      className="votacao-etapa-1-footer-actions__export"
                      onClick={handleExportarPlanilha}
                    >
                      EXPORTAR PLANILHA
                    </button>
                  </div>
                </div>
              )}

              {tabAtiva === 'selecionadas' && totalSelecionadas > 0 && (
                <div className="votacao-etapa-1-footer-actions votacao-etapa-1-footer-actions--top">
                  <div className="votacao-etapa-1-footer-actions__right-group">
                    <button
                      type="button"
                      className="votacao-etapa-1-footer-actions__send"
                      onClick={handleAbrirModalJustificativa}
                      disabled={!canSendVotes}
                      aria-disabled={!canSendVotes}
                    >
                      ENVIAR SELEÇÃO
                    </button>
                  </div>
                </div>
              )}

              <div className="votacao-etapa-1-list">
                {loadingIniciativasApi && (
                  <p className="votacao-etapa-1-list__empty">Carregando iniciativas...</p>
                )}

                {!loadingIniciativasApi && erroIniciativasApi && (
                  <div className="votacao-etapa-1-empty-tab" role="alert" aria-live="assertive">
                    <h2 className="votacao-etapa-1-empty-tab__title">{erroIniciativasApi}</h2>
                    <button
                      type="button"
                      className="votacao-etapa-1-footer-actions__export"
                      onClick={() => {
                        if (categoriaAtual?.isMad) {
                          carregarIniciativasMad();
                        } else {
                          carregarIniciativasEtapa1();
                        }
                      }}
                    >
                      TENTAR NOVAMENTE
                    </button>
                  </div>
                )}

                {!loadingIniciativasApi && !erroIniciativasApi && iniciativasRenderizadas.length === 0 && (
                  <p className="votacao-etapa-1-list__empty">Nenhuma iniciativa encontrada para os filtros selecionados.</p>
                )}

                {!loadingIniciativasApi && !erroIniciativasApi && iniciativasRenderizadas.map((iniciativa) => {
                  const isSelecionada = selecionadas.includes(iniciativa.id);
                  const corCategoriaHex = getCategoriaCorHex(iniciativa.categoryPremio);
                  const confirmados = iniciativa.participantes.filter((p) => p.confirmado).length;
                  const totalParticipantes = iniciativa.participantes.length;
                  const selectedCompliment = selectedCompliments[iniciativa.id] || '';
                  const isFavorited = favoritedIds.has(iniciativa.id);
                  const isComplimentOpen = complimentOpenId === iniciativa.id;

                  return (
                    <article
                      key={iniciativa.id}
                      className={`votacao-etapa-1-card ${isSelecionada ? 'is-selected' : ''}`}
                    >
                      {!isSelecionada && (
                        <div className="votacao-etapa-1-card__top">
                          <label className="votacao-etapa-1-card__select">
                            <input
                              type="checkbox"
                              checked={false}
                              onChange={() => {
                                if (!atingiuLimite) toggleSelecao(iniciativa.id);
                              }}
                              disabled={atingiuLimite}
                            />
                            <span className="votacao-etapa-1-card__select-indicator" aria-hidden="true">&#10003;</span>
                            {atingiuLimite ? `Limite atingido (${maxSelecionadas} de ${totalIniciativas})` : 'Selecionar'}
                          </label>
                        </div>
                      )}

                      <div className="votacao-etapa-1-card__body">
                        {/* Coluna esquerda - mídia, documentação, feedback */}
                        <div className="votacao-etapa-1-card__left">
                          <div className="votacao-etapa-1-card__video-container">
                            {iniciativa.videoUrl ? (
                              <a
                                href={iniciativa.videoUrlOriginal || iniciativa.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="votacao-etapa-1-card__video-link"
                              >
                                <img src={iniciativa.imagem} alt={iniciativa.titulo} className="votacao-etapa-1-card__video-thumbnail" />
                              </a>
                            ) : (
                              <img src={iniciativa.imagem} alt={iniciativa.titulo} className="votacao-etapa-1-card__video-thumbnail" />
                            )}
                          </div>

                          {iniciativa.documentacao && (
                            <div className="votacao-etapa-1-card__documentacao">
                              <p className="votacao-etapa-1-card__documentacao-label">
                                Documentação complementar da realização
                              </p>
                              <button className="votacao-etapa-1-card__download-btn" type="button">
                                <IoCloudDownloadOutline size={20} aria-hidden="true" />
                                <span>DOWNLOAD</span>
                              </button>
                            </div>
                          )}

                          {(iniciativa.elogiosPorTipo.length > 0 || iniciativa.totalFavoritos > 0) && (
                            <div className="votacao-etapa-1-card__feedback-box">
                              <h3 className="votacao-etapa-1-card__feedback-title">Feedback na iniciativa:</h3>
                              <div className="votacao-etapa-1-card__feedback-list">
                                {iniciativa.elogiosPorTipo.map((item, index) => (
                                  <div key={index} className="votacao-etapa-1-card__feedback-item">
                                    <span className="votacao-etapa-1-card__feedback-count">
                                      {String(item.quantidade).padStart(2, '0')}
                                    </span>
                                    <span className="votacao-etapa-1-card__feedback-label">{item.elogio}</span>
                                  </div>
                                ))}
                                {iniciativa.totalFavoritos > 0 && (
                                  <div className="votacao-etapa-1-card__feedback-item">
                                    <span className="votacao-etapa-1-card__feedback-count">
                                      {String(iniciativa.totalFavoritos).padStart(2, '0')}
                                    </span>
                                    <span className="votacao-etapa-1-card__feedback-label">Salvos nas favoritas</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Coluna direita - conteúdo */}
                        <div className="votacao-etapa-1-card__right">
                          <div className="votacao-etapa-1-card__header">
                            <p className="votacao-etapa-1-card__tema">{iniciativa.tema.toUpperCase()}</p>
                            <div
                              className={`votacao-etapa-1-card__badge tag-${iniciativa.corCategoria}`}
                              style={{ backgroundColor: corCategoriaHex }}
                            >
                              {iniciativa.categoriaTag}
                            </div>
                          </div>

                          <h3 className="votacao-etapa-1-card__title">{iniciativa.titulo}</h3>

                          <div className="votacao-etapa-1-card__info-box">
                            <div className="votacao-etapa-1-card__info-item">
                              <span className="votacao-etapa-1-card__info-label">Cadastrado por:</span>
                              <span className="votacao-etapa-1-card__info-value">
                                {iniciativa.cadastradoPor}, em {iniciativa.cadastradoData}
                              </span>
                            </div>
                            <div className="votacao-etapa-1-card__info-item">
                              <span className="votacao-etapa-1-card__info-label">Inscrito por:</span>
                              <span className="votacao-etapa-1-card__info-value">
                                {iniciativa.inscritoPor}, em {iniciativa.inscritoData}
                              </span>
                            </div>
                          </div>

                          <div className="votacao-etapa-1-card__question">
                            <h3 className="votacao-etapa-1-card__question-title">
                              {iniciativa.resumo}
                            </h3>
                            <p className="votacao-etapa-1-card__question-text">
                              {iniciativa.resumoTexto}
                            </p>
                          </div>

                          <div className="votacao-etapa-1-card__participantes" role="region" aria-label="Participantes da realização">
                            <h3 className="votacao-etapa-1-card__participantes-title">
                              Participantes da realização ({confirmados} de {totalParticipantes} confirmados)
                            </h3>
                            <div className="votacao-etapa-1-card__participantes-grid" role="list">
                              {iniciativa.participantes.map((participante, idx) => (
                                <div
                                  key={`${participante.nome}-${idx}`}
                                  className={`votacao-etapa-1-card__participante ${
                                    participante.confirmado || participante.isOwner ? 'confirmado' : 'pendente'
                                  }`}
                                  role="listitem"
                                >
                                  <div className="votacao-etapa-1-card__participante-content">
                                    <FaUser className="votacao-etapa-1-card__participante-avatar" size={14} aria-hidden="true" />
                                    {(participante.confirmado || participante.isOwner) && (
                                      <FaCheck className="votacao-etapa-1-card__participante-check" size={14} aria-hidden="true" />
                                    )}
                                    <span className="votacao-etapa-1-card__participante-nome">
                                      {participante.nome} ({participante.matricula})
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="votacao-etapa-1-card__action-bar">
                            <div className="votacao-etapa-1-card__action-left">
                              <div className="votacao-etapa-1-card__icon-wrapper">
                                <BsEmojiSmile
                                  size={20}
                                  aria-hidden="true"
                                  style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }}
                                />
                                {iniciativa.totalElogios > 0 && (
                                  <span className="votacao-etapa-1-card__badge-count">{iniciativa.totalElogios}</span>
                                )}
                              </div>
                              <div
                                className="votacao-etapa-1-card__compliment-container"
                                ref={isComplimentOpen ? complimentRef : undefined}
                              >
                                <button
                                  className="votacao-etapa-1-card__compliment-dropdown"
                                  onClick={() => setComplimentOpenId(isComplimentOpen ? null : iniciativa.id)}
                                  aria-expanded={isComplimentOpen}
                                  aria-haspopup="listbox"
                                  style={{
                                    borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                                    color: selectedCompliment ? '#5D5FEF' : '#666666'
                                  }}
                                >
                                  <span>{selectedCompliment || 'Faça um elogio!'}</span>
                                  <IoChevronDown size={16} aria-hidden="true" />
                                </button>
                                {isComplimentOpen && (
                                  <div className="votacao-etapa-1-card__compliment-menu" role="listbox">
                                    {COMPLIMENT_OPTIONS.map((compliment) => (
                                      <button
                                        key={compliment}
                                        className="votacao-etapa-1-card__compliment-option"
                                        role="option"
                                        aria-selected={selectedCompliment === compliment}
                                        onClick={() => handleComplimentSelect(iniciativa.id, compliment)}
                                      >
                                        {compliment}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="votacao-etapa-1-card__action-right">
                              <div className="votacao-etapa-1-card__icon-wrapper">
                                <button
                                  className="votacao-etapa-1-card__action-icon-btn"
                                  aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                                  aria-pressed={isFavorited}
                                  onClick={() => handleToggleFavorite(iniciativa.id)}
                                  style={{ color: isFavorited ? '#5D5FEF' : '#666666' }}
                                >
                                  {isFavorited
                                    ? <MdBookmark size={20} aria-hidden="true" />
                                    : <MdBookmarkBorder size={20} aria-hidden="true" />
                                  }
                                </button>
                                {iniciativa.totalFavoritos > 0 && (
                                  <span className="votacao-etapa-1-card__badge-count">{iniciativa.totalFavoritos}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {isSelecionada && (
                            <div className="votacao-etapa-1-card__action-bottom">
                              <button
                                type="button"
                                className="votacao-etapa-1-card__remove-btn votacao-etapa-1-card__remove-btn--inline"
                                onClick={() => removerSelecao(iniciativa.id)}
                              >
                                REMOVER SELEÇÃO
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              {tabAtiva === 'selecionadas' && totalSelecionadas > 0 && (
                <div className="votacao-etapa-1-footer-actions">
                  <div className="votacao-etapa-1-footer-actions__right-group">
                    <button
                      type="button"
                      className="votacao-etapa-1-footer-actions__send"
                      onClick={handleAbrirModalJustificativa}
                      disabled={!canSendVotes}
                      aria-disabled={!canSendVotes}
                    >
                      ENVIAR SELEÇÃO
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {totalSelecionadas > 0 && tabAtiva !== 'selecionadas' && (
        <aside className="votacao-etapa-1-floating-card" role="status" aria-live="polite">
          <h4>
            {(isCategoriaSimplifique || isCategoriaElbb)
              ? `${totalSelecionadas} de ${maxSelecionadas} iniciativas selecionadas!`
              : `${percentualSelecionado}% das iniciativas foram selecionadas!`}
          </h4>
          <p>
            {atingiuMinimo
              ? 'A aba de iniciativas selecionadas está habilitada para a seleção.'
              : (
                <>
                  {(isCategoriaSimplifique || isCategoriaElbb)
                    ? `É necessário selecionar ${maxSelecionadas} iniciativas antes de enviar `
                    : 'É necessário selecionar 50% das iniciativas antes de enviar '}
                  <button
                    type="button"
                    className="votacao-etapa-1-floating-card__link"
                    onClick={() => setTabAtiva('selecionadas')}
                  >
                    a seleção final.
                  </button>{' '}
                </>
              )}
          </p>
          {atingiuMinimo && (
            <div className="votacao-etapa-1-floating-card__actions">
              <button type="button" onClick={acessarSelecionadas}>ACESSAR SELECIONADAS</button>
            </div>
          )}
        </aside>
      )}
      </>)}
    </div>
  );
}
